import React, { useState, useMemo } from 'react';
import { Button } from 'antd';
import { LeftOutlined, RightOutlined, WarningOutlined } from '@ant-design/icons';
import moment from 'moment';
import { priorityMeta, dueMeta, TB_COLORS } from './utils';
import { Assignees } from './bits';

const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const LEGEND = [
  { label: 'High', color: '#f79009' },
  { label: 'Medium', color: '#98a2b3' },
  { label: 'Low', color: '#d0d5dd' },
  { label: 'Completed', color: '#067647' },
  { label: 'Overdue', color: '#f04438' },
];

const dotColor = (t) => {
  if (t.status === 'completed') return '#067647';
  if (dueMeta(t.due_date, t.status)?.overdue) return '#f04438';
  return priorityMeta(t.priority).color;
};

const CalendarView = ({ tasks, propertyName, areaMap, onEdit }) => {
  const [cursor, setCursor] = useState(moment().startOf('day'));
  const [mode, setMode] = useState('month');

  const tasksByDay = useMemo(() => {
    const map = {};
    tasks.forEach((t) => {
      if (!t.due_date) return;
      const d = moment(t.due_date);
      if (!d.isValid()) return;
      const key = d.format('YYYY-MM-DD');
      (map[key] = map[key] || []).push(t);
    });
    return map;
  }, [tasks]);

  const days = useMemo(() => {
    const out = [];
    if (mode === 'week') {
      const start = cursor.clone().startOf('isoWeek');
      for (let i = 0; i < 7; i += 1) out.push(start.clone().add(i, 'days'));
    } else {
      const start = cursor.clone().startOf('month').startOf('isoWeek');
      const end = cursor.clone().endOf('month').endOf('isoWeek');
      const d = start.clone();
      while (d.isSameOrBefore(end)) {
        out.push(d.clone());
        d.add(1, 'days');
      }
    }
    return out;
  }, [cursor, mode]);

  const step = (dir) => setCursor((c) => c.clone().add(dir, mode === 'week' ? 'weeks' : 'months'));

  const monthTasks = tasks.filter(
    (t) => t.due_date && moment(t.due_date).isSame(cursor, 'month')
  );
  const overdue = tasks
    .filter((t) => dueMeta(t.due_date, t.status)?.overdue)
    .sort((a, b) => moment(a.due_date).diff(moment(b.due_date)));
  const todayTasks = tasksByDay[moment().format('YYYY-MM-DD')] || [];
  const today = moment().format('YYYY-MM-DD');

  const dayCell = (d) => {
    const key = d.format('YYYY-MM-DD');
    const list = tasksByDay[key] || [];
    const inMonth = mode === 'week' || d.isSame(cursor, 'month');
    const isToday = key === today;
    const shown = list.slice(0, mode === 'week' ? 12 : 3);
    return (
      <div
        key={key}
        style={{
          border: '1px solid #f2f4f7',
          minHeight: mode === 'week' ? 360 : 96,
          padding: 6,
          background: inMonth ? '#fff' : '#fafbfc',
        }}
      >
        <div
          style={{
            fontSize: 13,
            fontWeight: isToday ? 700 : 500,
            color: inMonth ? TB_COLORS.text : '#98a2b3',
            textAlign: 'right',
            marginBottom: 4,
          }}
        >
          <span
            style={
              isToday
                ? { background: '#0B2A1B', color: '#fff', borderRadius: 6, padding: '1px 6px' }
                : undefined
            }
          >
            {d.date()}
          </span>
        </div>
        {shown.map((t) => (
          <div
            key={t.id}
            onClick={() => onEdit(t)}
            title={t.title}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              background: '#f9fafb',
              borderRadius: 5,
              padding: '2px 5px',
              marginBottom: 3,
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor(t), flex: '0 0 auto' }} />
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: t.status === 'completed' ? '#98a2b3' : TB_COLORS.text,
                textDecoration: t.status === 'completed' ? 'line-through' : 'none',
              }}
            >
              {t.title}
            </span>
          </div>
        ))}
        {list.length > shown.length ? (
          <div style={{ fontSize: 11, color: TB_COLORS.subtext }}>+{list.length - shown.length} more</div>
        ) : null}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <div style={{ flex: 1, minWidth: 0, border: '1px solid #eaecf0', borderRadius: 12, padding: 16, background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Button icon={<LeftOutlined />} onClick={() => step(-1)} />
            <Button icon={<RightOutlined />} onClick={() => step(1)} />
            <span style={{ fontWeight: 700, fontSize: 18, marginLeft: 4 }}>
              {mode === 'week'
                ? `${cursor.clone().startOf('isoWeek').format('D MMM')} – ${cursor.clone().endOf('isoWeek').format('D MMM YYYY')}`
                : cursor.format('MMMM YYYY')}
            </span>
            <Button size="small" onClick={() => setCursor(moment().startOf('day'))}>
              Today
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ background: '#f2f4f7', borderRadius: 16, padding: '2px 10px', fontSize: 13, color: TB_COLORS.subtext }}>
              {monthTasks.length} tasks this {mode}
            </span>
            {overdue.length ? (
              <span style={{ background: '#fef3f2', borderRadius: 16, padding: '2px 10px', fontSize: 13, color: '#b42318' }}>
                <WarningOutlined /> {overdue.length} overdue
              </span>
            ) : null}
            <Button.Group>
              <Button type={mode === 'month' ? 'primary' : 'default'} onClick={() => setMode('month')}>
                Month
              </Button>
              <Button type={mode === 'week' ? 'primary' : 'default'} onClick={() => setMode('week')}>
                Week
              </Button>
            </Button.Group>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
          {LEGEND.map((l) => (
            <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: TB_COLORS.subtext }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: l.color }} /> {l.label}
            </span>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
          {WD.map((w) => (
            <div key={w} style={{ textAlign: 'center', fontSize: 12, fontWeight: 600, color: TB_COLORS.subtext, padding: '6px 0' }}>
              {w}
            </div>
          ))}
          {days.map(dayCell)}
        </div>
      </div>

      <div style={{ flex: '0 0 300px', maxWidth: 300 }}>
        <div style={{ border: '1px solid #eaecf0', borderRadius: 12, padding: 16, background: '#fff', marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>Today</div>
          <div style={{ color: TB_COLORS.subtext, fontSize: 13, marginBottom: 10 }}>{todayTasks.length} tasks</div>
          {todayTasks.length === 0 ? (
            <div style={{ color: '#98a2b3', fontSize: 13 }}>Nothing due today.</div>
          ) : (
            todayTasks.map((t) => (
              <div key={t.id} onClick={() => onEdit(t)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', cursor: 'pointer', borderTop: '1px solid #f2f4f7' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor(t), flex: '0 0 auto' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                  <div style={{ fontSize: 12, color: TB_COLORS.subtext }}>{[propertyName, areaMap?.[t.primary_object_id]].filter(Boolean).join(' · ')}</div>
                </div>
                <Assignees task={t} size={22} />
              </div>
            ))
          )}
        </div>

        <div style={{ border: '1px solid #eaecf0', borderRadius: 12, padding: 16, background: '#fff' }}>
          <div style={{ fontWeight: 700, fontSize: 13, letterSpacing: 0.5, color: TB_COLORS.subtext, textTransform: 'uppercase', marginBottom: 8 }}>
            Needs Attention
          </div>
          {overdue.length === 0 ? (
            <div style={{ color: '#98a2b3', fontSize: 13 }}>Nothing overdue.</div>
          ) : (
            overdue.slice(0, 8).map((t) => (
              <div key={t.id} onClick={() => onEdit(t)} style={{ padding: '8px 0', cursor: 'pointer', borderTop: '1px solid #f2f4f7' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor(t), flex: '0 0 auto' }} />
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                </div>
                <div style={{ fontSize: 12, color: '#b42318', marginLeft: 16 }}>
                  Overdue · {moment(t.due_date).format('D MMM')}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
